import {
    Injector,
    APP_INITIALIZER,
    EnvironmentInjector,
    ConstructorProvider,
    ExistingProvider,
    FactoryProvider,
    StaticClassProvider,
    ValueProvider
} from '@angular/core';
import { Router } from '@angular/router';
import { PluginMetadataApiService } from 'app/apis/plugin-metadata-api.service';
import { ActivityRegistryService } from 'app/authoring/registries/activity-registry.service';
import { TransformationRegistryService } from 'app/authoring/registries/transformation-registry.service';
import { DATAFLOW_PARSING_STRATEGY, ParsingStrategy } from 'app/dataflow/dataflowPO.parser.models';
import { LoggerService } from 'app/logger/logger.service';
import { ArmTemplateGeneratorService } from 'app/model/services/arm-template-generator.service';
import { BaseExtensiveValidationService } from 'app/model/services/base-extensive-validation.service';
import { BaseFileClientProviderService } from 'app/model/services/base-file-client-provider.service';
import { ModelService } from 'app/model/services/model.service';
import { PublishConfigService } from 'app/model/services/publisher/publish-config.service';
import { UndoRedoService } from 'app/model/services/undo-redo.service';
import { DataflowFormatDynamicLoaderService } from 'app/registries/dataflow/dataflow-format-dynamic-loader.service';
import { DataflowFormatRegistryService } from 'app/registries/dataflow/dataflow-format-registry.service';
import { CopySinkRegistryService } from 'app/registries/dataset/copy-sink-registry.service';
import { CopySourceRegistryService } from 'app/registries/dataset/copy-source-registry.service';
import { DatasetDynamicLoaderService } from 'app/registries/dataset/dataset-dynamic-loader.service';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { LinkedServiceDynamicLoaderService } from 'app/registries/linked-service/linked-service-dynamic-loader.service';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { PrivateEndpointRegistryService } from 'app/registries/private-endpoint/private-endpoint-registry.service';
import { BaseDataProviderService } from 'app/services/base-data-provider.service';
import { DataFlowCleanupService } from 'app/services/dataflow-cleanup.service';
import { DynamicConnectorService } from 'app/services/dynamic-connector.service';
import {
    AzLgcAppExpLangNodejsService,
    initializeAzureLogicAppExpressionLanguage
} from 'app/services/azure-logic-app-lang/az-lgc-app-exp-lang-nodejs-service';
import { ExpressionResolverService } from 'app/services/expression-resolver-service';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { IFrameMessengerService } from 'app/services/iframe-messenger.service';
import { MessagingService } from 'app/services/messaging/messaging-service';
import { MetadataModeService } from 'app/services/metadata-mode.service';
import { PrimaryResourceUrlInformationService } from 'app/services/primary-resource-url-information.service';
import { WorkspaceUrlInformationService } from 'app/services/workspace-url-information.service';
import { normalizePath } from 'app/shared/utils/string.util';
import { CmdApiDataProviderService } from './services/cmd-api-data-provider.service';
import { CmdApiLoggerService } from './services/cmd-api-logger.service';
import { FileClientProviderService } from './services/file-client-provider.service';
import { LocalFileClientService } from './services/local-file-client.service';
import { ROOT_FOLDER, ARM_TEMPLATE_OPTIONS, IARMTemplateOptions } from './services/local-resource-provider.tokens';
import { MetadataClientFactoryService } from 'app/services/metadata-client/metadata-client-factory.service';
import { FactoryCacheService } from 'app/services/factory-cache.service';
import { PluginMetadataProcessService } from 'app/services/plugin-metadata-process.service';
import { UserDefinedFunctionsService } from 'app/services/user-defined-functions.service';
import { IFrameService } from 'app/services/iframe.service';
import { ModelChangeService } from 'app/model/services/model-change.service';
import { IdentityService } from 'app/model/services/identity.service';
import { RESOURCE_CONTEXT_DATA, RESOURCE_CONTEXT_DATA_TOKEN } from 'app/multi-contexts/resource-context.typings';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { CmdApiExposureControlService } from './services/cmd-api-exposure-control.service';

export function getCmdApiInjector(
    rootFolder: string,
    armTemplateOptions: IARMTemplateOptions,
    factoryId: string
): Injector {
    rootFolder = normalizePath(rootFolder);
    const ret: Injector = Injector.create({
        providers: [
            ...getCmdApiProviders(rootFolder, armTemplateOptions, factoryId),
            { provide: EnvironmentInjector, useFactory: () => ret }
        ]
    });
    return ret;
}

export type CmdApiAppProvider =
    | ValueProvider
    | ExistingProvider
    | StaticClassProvider
    | ConstructorProvider
    | FactoryProvider;

export function getCmdApiProviders(
    rootFolder: string,
    armTemplateOptions: IARMTemplateOptions,
    factoryId: string
): CmdApiAppProvider[] {
    rootFolder = normalizePath(rootFolder);
    const ret: CmdApiAppProvider[] = [
        { provide: ROOT_FOLDER, useValue: rootFolder },
        { provide: ARM_TEMPLATE_OPTIONS, useValue: armTemplateOptions },
        { provide: DATAFLOW_PARSING_STRATEGY, useValue: ParsingStrategy.Local },
        { provide: Router, useValue: {} },
        {
            provide: RESOURCE_CONTEXT_DATA,
            useValue: {
                contextId: factoryId,
                contextType: 'cmdApiApp'
            }
        },
        {
            provide: RESOURCE_CONTEXT_DATA_TOKEN,
            useValue: {
                contextId: factoryId,
                contextType: 'cmdApiApp'
            }
        },
        { provide: UndoRedoService, deps: [ModelService] },
        { provide: MessagingService, deps: [] },
        { provide: 'MessagingService', useExisting: MessagingService },
        { provide: MetadataModeService, deps: [] },
        { provide: 'MetadataModeService', useExisting: MetadataModeService },
        { provide: DataFlowCleanupService, deps: [ModelService] },
        { provide: 'DataFlowCleanupService', useExisting: DataFlowCleanupService },
        { provide: LinkedServiceRegistryService, deps: [RESOURCE_CONTEXT_DATA, ExposureControlService] },
        {
            provide: 'LinkedServiceRegistryService',
            useExisting: LinkedServiceRegistryService
        },
        { provide: DatasetRegistryService, deps: [ExposureControlService] },
        { provide: ActivityRegistryService, deps: [RESOURCE_CONTEXT_DATA, ExposureControlService] },
        { provide: 'ActivityRegistryService', useExisting: ActivityRegistryService },
        { provide: TransformationRegistryService, deps: [] },
        { provide: PrivateEndpointRegistryService, deps: [] },
        { provide: DataflowFormatRegistryService, deps: [DatasetRegistryService, ExposureControlService] },
        { provide: 'DataflowFormatRegistryService', useExisting: DataflowFormatRegistryService },
        { provide: MetadataClientFactoryService, useValue: {} },
        { provide: CopySourceRegistryService, deps: [] },
        { provide: CopySinkRegistryService, deps: [] },
        { provide: PluginMetadataApiService, useValue: {} },
        { provide: PluginMetadataProcessService, deps: [] },
        { provide: 'PluginMetadataProcessService', useExisting: PluginMetadataProcessService },
        { provide: BaseExtensiveValidationService, useValue: {} },
        { provide: 'BaseExtensiveValidationService', useValue: {} },
        {
            provide: DataflowFormatDynamicLoaderService,
            deps: [DataflowFormatRegistryService, LinkedServiceRegistryService, ExposureControlService]
        },
        { provide: LinkedServiceDynamicLoaderService, deps: [LinkedServiceRegistryService] },
        {
            provide: DynamicConnectorService,
            deps: [
                DatasetDynamicLoaderService,
                LoggerService,
                PluginMetadataApiService,
                LinkedServiceDynamicLoaderService,
                DataflowFormatDynamicLoaderService,
                PluginMetadataProcessService
            ]
        },
        {
            provide: DatasetDynamicLoaderService,
            deps: [
                DatasetRegistryService,
                LinkedServiceRegistryService,
                CopySourceRegistryService,
                CopySinkRegistryService
            ]
        },
        { provide: CmdApiLoggerService, deps: [] },
        {
            provide: ExpressionResolverService,
            useClass: AzLgcAppExpLangNodejsService,
            deps: [LoggerService, ModelService, ExposureControlService]
        },
        {
            provide: 'ExpressionResolverService',
            useExisting: ExpressionResolverService
        },
        { provide: LoggerService, useExisting: CmdApiLoggerService, deps: [] },
        { provide: 'LoggerService', useExisting: LoggerService },
        { provide: IFrameMessengerService, useValue: {} },
        { provide: IFrameService, useValue: {} },
        { provide: LocalFileClientService, deps: [LoggerService, ROOT_FOLDER] },
        {
            provide: CmdApiDataProviderService,
            deps: [
                RESOURCE_CONTEXT_DATA,
                LoggerService,
                FileClientProviderService,
                PrimaryResourceUrlInformationService,
                IdentityService,
                ExposureControlService
            ]
        },
        {
            provide: ExposureControlService,
            useClass: CmdApiExposureControlService,
            deps: []
        },
        {
            provide: 'ExposureControlService',
            useExisting: ExposureControlService
        },
        { provide: FileClientProviderService, deps: [Injector] },
        { provide: BaseFileClientProviderService, useExisting: FileClientProviderService },
        { provide: 'BaseFileClientProviderService', useExisting: FileClientProviderService },
        { provide: BaseDataProviderService, useExisting: CmdApiDataProviderService },
        { provide: FactoryUrlInformationService, deps: [Injector] },
        { provide: 'FactoryUrlInformationService', useExisting: FactoryUrlInformationService },
        { provide: WorkspaceUrlInformationService, deps: [Injector] },
        {
            provide: PublishConfigService,
            deps: [BaseFileClientProviderService, MetadataModeService, LoggerService]
        },
        { provide: FactoryCacheService, deps: [] },
        { provide: 'FactoryCacheService', useExisting: FactoryCacheService },
        {
            provide: ArmTemplateGeneratorService,
            deps: [
                RESOURCE_CONTEXT_DATA,
                ModelService,
                PublishConfigService,
                PrimaryResourceUrlInformationService,
                DataFlowCleanupService,
                LoggerService,
                ARM_TEMPLATE_OPTIONS,
                IdentityService,
                Injector
            ]
        },
        {
            provide: PrimaryResourceUrlInformationService,
            deps: [FactoryUrlInformationService, WorkspaceUrlInformationService]
        },
        { provide: 'PrimaryResourceUrlInformationService', useExisting: PrimaryResourceUrlInformationService },
        {
            provide: ModelService,
            deps: [
                RESOURCE_CONTEXT_DATA,
                IFrameMessengerService,
                FactoryUrlInformationService,
                BaseDataProviderService,
                IFrameService,
                IdentityService,
                DynamicConnectorService
            ]
        },
        // Parts in our code inject services as below due to circular references.
        { provide: 'ModelService', useExisting: ModelService },
        { provide: 'DatasetRegistryService', useExisting: DatasetRegistryService },
        { provide: UserDefinedFunctionsService, deps: [RESOURCE_CONTEXT_DATA] },
        { provide: 'UserDefinedFunctionsService', useExisting: UserDefinedFunctionsService },
        { provide: IdentityService, deps: [RESOURCE_CONTEXT_DATA] },
        { provide: 'IdentityService', useExisting: IdentityService },
        {
            // https://angular.io/api/core/APP_INITIALIZER
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: () => initializeAzureLogicAppExpressionLanguage
        },
        { provide: ModelChangeService, deps: [] }
    ];
    return ret;
}
